import { Suspense, type ComponentType } from 'react';
import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import { AppLayout } from '@shared/layouts/AppLayout';
import { AdminLayout } from '@shared/layouts/AdminLayout';
import { AuthLayout } from '@shared/layouts/AuthLayout';
import { AuthGuard } from '@routes/guards/AuthGuard';
import { GuestGuard } from '@routes/guards/GuestGuard';
import { AdminGuard } from '@routes/guards/AdminGuard';
import { LoadingState } from '@shared/components';
import { HOME_ROUTE } from '@config/routes';
import { lazyRoute } from '@/app/lazyRoute';

const LandingPage = lazyRoute(() => import('@features/landing/pages/LandingPage'));
const LoginPage = lazyRoute(() => import('@features/auth/pages/LoginPage'));
const AdminLoginPage = lazyRoute(() => import('@features/auth/pages/AdminLoginPage'));
const ProjectBriefPage = lazyRoute(() => import('@features/projects/pages/ProjectBriefPage'));
const HomePage = lazyRoute(() => import('@features/dashboard/pages/HomePage'));
const ProjectCreatePage = lazyRoute(() => import('@features/projects/pages/ProjectCreatePage'));
const PrototypePage = lazyRoute(() => import('@features/prototypes/pages/PrototypePage'));
const ChangePasswordPage = lazyRoute(() => import('@features/auth/pages/ChangePasswordPage'));
const SessionDetailPage = lazyRoute(() => import('@features/sessions/pages/SessionDetailPage'));
const MySessionsPage = lazyRoute(() => import('@features/sessions/pages/MySessionsPage'));
const McpPage = lazyRoute(() => import('@features/mcp/pages/McpPage'));
const InterviewPage = lazyRoute(() => import('@features/interview/pages/InterviewPage'));
const ChatInterviewPage = lazyRoute(() => import('@features/interview/pages/ChatInterviewPage'));
const AdminHomePage = lazyRoute(() => import('@features/admin/pages/AdminHomePage'));
const UsersPage = lazyRoute(() => import('@features/admin/pages/UsersPage'));
const UserFormPage = lazyRoute(() => import('@features/admin/pages/UserFormPage'));
const AdminDomainsPage = lazyRoute(() => import('@features/admin/pages/AdminDomainsPage'));
const DomainFormPage = lazyRoute(() => import('@features/admin/pages/DomainFormPage'));
const ProjectsOverviewPage = lazyRoute(() => import('@features/admin/pages/ProjectsOverviewPage'));
const SummariesPage = lazyRoute(() => import('@features/admin/pages/SummariesPage'));
const CapabilityCataloguePage = lazyRoute(
  () => import('@features/admin/pages/CapabilityCataloguePage'),
);
const NotFoundPage = lazyRoute(() => import('@features/misc/pages/NotFoundPage'));

function withSuspense(Component: ComponentType): React.JSX.Element {
  return (
    <Suspense fallback={<LoadingState fullscreen />}>
      <Component />
    </Suspense>
  );
}

const routes: RouteObject[] = [
  { path: '/', element: withSuspense(LandingPage) },
  {
    element: <GuestGuard />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: '/login', element: withSuspense(LoginPage) },
          { path: '/admin', element: withSuspense(AdminLoginPage) },
        ],
      },
    ],
  },
  {
    element: <AuthGuard />,
    children: [
      {
        element: <AppLayout />,
        children: [
          { path: HOME_ROUTE, element: withSuspense(HomePage) },
          { path: '/projects/new', element: withSuspense(ProjectCreatePage) },
          { path: '/sessions', element: withSuspense(MySessionsPage) },
          { path: '/me/password', element: withSuspense(ChangePasswordPage) },
          { path: '/mcp', element: withSuspense(McpPage) },
          { path: '/projects/:projectId', element: withSuspense(ProjectBriefPage) },
          { path: '/projects/:projectId/prototype', element: withSuspense(PrototypePage) },
          {
            path: '/projects/:projectId/sessions/:sessionId',
            element: withSuspense(SessionDetailPage),
          },
        ],
      },
      { path: '/projects/:projectId/interview', element: withSuspense(InterviewPage) },
      { path: '/projects/:projectId/chat', element: withSuspense(ChatInterviewPage) },
      {
        element: <AdminGuard />,
        children: [
          {
            element: <AdminLayout />,
            children: [
              { path: '/admin/home', element: withSuspense(AdminHomePage) },
              { path: '/admin/users', element: withSuspense(UsersPage) },
              { path: '/admin/users/new', element: withSuspense(UserFormPage) },
              { path: '/admin/users/:userId/edit', element: withSuspense(UserFormPage) },
              { path: '/admin/domains', element: withSuspense(AdminDomainsPage) },
              { path: '/admin/domains/new', element: withSuspense(DomainFormPage) },
              { path: '/admin/domains/:domainId/edit', element: withSuspense(DomainFormPage) },
              { path: '/admin/projects', element: withSuspense(ProjectsOverviewPage) },
              { path: '/admin/summaries', element: withSuspense(SummariesPage) },
              { path: '/admin/capabilities', element: withSuspense(CapabilityCataloguePage) },
            ],
          },
        ],
      },
    ],
  },
  { path: '*', element: withSuspense(NotFoundPage) },
];

export const router = createBrowserRouter(routes);
